import {
  FacetConfig,
  FacetLayoutSection,
  FacetResponses,
  FacetRootStore,
  FacetState,
  FacetStore,
  FieldName,
  LayoutField,
  LookupData,
  LookupSourceIdentifier,
  MediaSourceId,
  MediaSourceData,
  FacetSectionStore,
} from "@globalgiving/vast-frontend-common"
import { action, comparer, computed, observable } from "mobx"
import { Category } from "~/stores/domain/objects"
import { BANKING_DETAILS_FACET_NAMES, CategoryStore, InvalidFacetInfo } from "~/types"

export type OrganizationUpdateFacetStore = Pick<
  FacetStore,
  "config" | "isValid" | "layoutSections" | "submissionValues" | "lookupSources" | "fileIds"
>

export type OrganizationUpdateCategoryStore = Pick<CategoryStore, "config" | "update"> & {
  facets: Array<OrganizationUpdateFacetStore>
}

export class OrganizationUpdateStore implements FacetRootStore {
  @observable accessor categories: CategoryStore[] = []
  @observable accessor lookupSources: Map<LookupSourceIdentifier, LookupData | null> = new Map()
  @observable accessor media: Map<MediaSourceId, MediaSourceData | null> = new Map()

  @computed
  get facets(): FacetStore[] {
    return this.categories.flatMap((category) => category.facets)
  }

  /**
   * @returns the first facet that failed to construct, if any.
   */
  @computed
  get invalidFacet(): InvalidFacetInfo | null {
    return this.categories.find((category) => category.invalidFacet)?.invalidFacet ?? null
  }

  /**
   * @returns Categories with facets that can be updated at any time.
   * The returned facets include only those fields that can be updated at any time. Banking
   * details facets are always excluded here, regardless of their canBeUpdatedAtAnyTime flag,
   * since changes to them must go through GlobalGiving's Financial Operations team.
   * Returns only some properties of FacetStore to simplify the usage in the components and reduce number of re-renderings.
   */
  @computed
  get categoriesUpdatedAtAnyTime(): OrganizationUpdateCategoryStore[] {
    return this.categories
      .map((category) => {
        const filteredCategory: OrganizationUpdateCategoryStore = { ...category }
        filteredCategory.facets = category.facets
          // Keep only facets that can be updated at any time
          .filter(({ config }) => config.canBeUpdatedAtAnyTime && !BANKING_DETAILS_FACET_NAMES.includes(config.name))
          // Keep only fields in facet that can be updated at any time
          .map((facet): OrganizationUpdateFacetStore => {
            const updatedAtAnyTimeFields = facet.config.updateAtAnyTimeFields || []

            return {
              config: facet.config,
              // Only fields that can be updated at any time are validated
              isValid: this.isValidForFieldsUpdatedAtAnyTime(facet.visibleSections, updatedAtAnyTimeFields),
              submissionValues: facet.submissionValues,
              layoutSections: facet.layoutSections
                // Filter the layout section fields to keep only those that can be updated at any time
                .map((section): FacetLayoutSection => {
                  const fieldsThatCanBeUpdated = this.filterFieldsUpdatedAtAnyTime(
                    section.items,
                    updatedAtAnyTimeFields
                  )

                  return {
                    ...section,
                    items: fieldsThatCanBeUpdated,
                  }
                })
                // Keep only those sections that have fields to show
                .filter((section) => !!section.items.length),
              lookupSources: facet.lookupSources,
              fileIds: facet.fileIds,
            }
          })

        return filteredCategory
      })
      .filter((category) => !!category.facets.length)
  }

  /**
   * @returns The entity's banking details facets (disbursementSelection, ach, check, wire,
   * fxecute). These are only a subset of the Disbursement Information category; other facets in that same
   * category may still be legitimately editable on this page.
   */
  @computed
  get bankingDetailsFacets(): FacetStore[] {
    return this.facets.filter((facet) => BANKING_DETAILS_FACET_NAMES.includes(facet.config.name))
  }

  /**
   * @returns Whether any banking details facet is currently unlocked for editing (state Input).
   */
  @computed
  get isBankingDetailsActiveTask(): boolean {
    return this.bankingDetailsFacets.some((facet) => facet.config.state === FacetState.Input)
  }

  /**
   * @returns The category the entity's banking details facets belong to.
   */
  @computed
  get bankingDetailsCategory(): CategoryStore | undefined {
    return this.categories.find((category) =>
      category.facets.some((facet) => BANKING_DETAILS_FACET_NAMES.includes(facet.config.name))
    )
  }

  constructor(facetConfigs?: FacetConfig[]) {
    if (facetConfigs) {
      this.categories = this.sortByCategory(facetConfigs)
    }
  }

  @action
  update(facetConfigs: FacetConfig[]): void {
    this.categories = this.sortByCategory(facetConfigs)
  }

  @action
  updateValues(responses: FacetResponses): void {
    Object.entries(responses).forEach(([key, values]) => {
      const targetFacet = this.facets.find((facet) => facet.config.name === key)
      if (targetFacet) {
        targetFacet.updateValues(values)
      }
    })
  }

  @action
  updateLookupSource(lookupSourceIdentifier: LookupSourceIdentifier, data: LookupData | null): void {
    this.lookupSources.set(lookupSourceIdentifier, data)
  }

  @action
  updateMedia(id: MediaSourceId, data: MediaSourceData | null): void {
    /* Skip the write when nothing actually changed. */
    if (comparer.structural(this.media.get(id), data)) {
      return
    }

    this.media.set(id, data)
  }

  /**
   * Groups the given facets by their `category_path` and creates a {@link Category}
   * instance for each distinct category.
   *
   * Each group of facets with the same `category_path` is converted into a
   * `Category` using the category path as the name (unique identifier) and the first facet's
   * `category` value as the label fallback.
   *
   * @param facets - The list of facet configurations to group into categories.
   * @returns An array of {@link Category} instances, one for each category path.
   */
  sortByCategory(facets: FacetConfig[]): Category[] {
    const categoryFacets = new Map<string, FacetConfig[]>()
    facets.forEach((facet) => {
      const category = categoryFacets.get(facet.category_path)
      if (category) {
        category.push(facet)
      } else {
        categoryFacets.set(facet.category_path, [facet])
      }
    })

    return Array.from(categoryFacets.entries()).map(([categoryPath, configs]) => {
      // All the facets inside one category have the same category name
      const categoryName = configs[0].category || categoryPath
      const categoryConfig = {
        categoryName: categoryPath,
        categoryLabel: categoryName,
        facets: configs,
      }

      return new Category(categoryConfig, this)
    })
  }

  /**
   * Recursively filters a layout field tree down to only the fields that can be updated at
   * any time, keeping dependent (child) fields alongside their parent where applicable.
   */
  private filterFieldsUpdatedAtAnyTime(fields: LayoutField[], updatedAtAnyTimeFields: FieldName[]): LayoutField[] {
    return fields
      .filter((layoutField) => updatedAtAnyTimeFields.includes(layoutField.item.config.name))
      .map((layoutField) => ({
        ...layoutField,
        children: this.filterFieldsUpdatedAtAnyTime(layoutField.children || [], updatedAtAnyTimeFields),
      }))
  }

  /**
   * A facet is only valid if its fields that can be updated at any time are valid.
   */
  private isValidForFieldsUpdatedAtAnyTime(
    sections: FacetSectionStore[],
    updatedAtAnyTimeFields: FieldName[]
  ): boolean {
    return sections
      .flatMap((section) => section.fields)
      .filter((field) => updatedAtAnyTimeFields.includes(field.config.name))
      .every((field) => field.isValid)
  }
}

export const organizationUpdateStore = new OrganizationUpdateStore()
